import { useCallback } from 'react';

import {
  useCreateProofreadingRuleMutation,
  useDeleteProofreadingRuleMutation,
  useListProofreadingRulesQuery,
  useUpdateProofreadingRuleMutation,
} from '../../../services/api/proofreadingService';
import {
  CreateProofreadingRule,
  ProofreadingRule,
} from '../../../services/types/proofreading-types';

export function useProofreadingRules(firmId: string) {
  // Fetch all rules for the firm
  const {
    data: rules = [],
    isLoading,
    error,
    refetch,
  } = useListProofreadingRulesQuery(
    { firmId },
    { refetchOnMountOrArgChange: true },
  );

  // Mutations
  const [createRuleMutation, { isLoading: isCreating }] =
    useCreateProofreadingRuleMutation();
  const [updateRuleMutation, { isLoading: isUpdating }] =
    useUpdateProofreadingRuleMutation();
  const [deleteRuleMutation, { isLoading: isDeleting }] =
    useDeleteProofreadingRuleMutation();

  // Create a new rule
  const createRule = useCallback(
    async (rule: CreateProofreadingRule): Promise<ProofreadingRule> => {
      const response = await createRuleMutation({
        firmId,
        data: rule,
      }).unwrap();
      refetch();
      return response;
    },
    [createRuleMutation, firmId, refetch],
  );

  // Update an existing rule
  const updateRule = useCallback(
    async (
      ruleId: string,
      rule: Partial<CreateProofreadingRule>,
    ): Promise<ProofreadingRule> => {
      const response = await updateRuleMutation({
        firmId,
        ruleId,
        data: rule,
      }).unwrap();
      refetch();
      return response;
    },
    [updateRuleMutation, firmId, refetch],
  );

  // Delete a rule
  const deleteRule = useCallback(
    async (ruleId: string): Promise<void> => {
      await deleteRuleMutation({ firmId, ruleId }).unwrap();
      refetch();
    },
    [deleteRuleMutation, firmId, refetch],
  );

  // Split rules into global and firm-specific
  const globalRules = rules.filter((rule) => rule.is_global);
  const firmRules = rules.filter((rule) => !rule.is_global);

  return {
    rules,
    globalRules,
    firmRules,
    isLoading: isLoading || isCreating || isUpdating || isDeleting,
    error,
    createRule,
    updateRule,
    deleteRule,
  };
}
